import { createFileRoute, Link } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QRCodeCanvas } from 'qrcode.react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type CommunityTier,
  type SignupRefUser,
  fetchCommunityTiers,
  fetchSignupRefUsers,
  setUserTier,
} from '@/api/admin-access';
import { fetchSignupRef } from '@/api/signup-refs';

export const Route = createFileRoute('/admin/signup-codes_/$code')({
  component: SignupCodeDetailPage,
});

function SignupCodeDetailPage() {
  const { code } = Route.useParams();

  const codeQuery = useQuery({
    queryKey: ['signup-ref', code],
    queryFn: () => fetchSignupRef(code),
    refetchOnWindowFocus: false,
  });

  const usersQuery = useQuery({
    queryKey: ['signup-ref-users', code],
    queryFn: () => fetchSignupRefUsers(code),
    refetchOnWindowFocus: false,
  });

  const tiersQuery = useQuery({
    queryKey: ['admin-tiers'],
    queryFn: fetchCommunityTiers,
    refetchOnWindowFocus: false,
  });

  const ref = codeQuery.data;
  const users = usersQuery.data ?? [];
  const tiers = tiersQuery.data ?? [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header + back */}
      <div className="px-6 py-4 border-b border-border">
        <Link
          to="/admin/signup-codes"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to codes
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold font-mono">{code}</h1>
          {ref && <Badge variant="secondary">{ref.count} signups</Badge>}
        </div>
        {ref && (
          <p className="text-sm text-muted-foreground">
            {ref.name}
            {ref.purpose ? ` — ${ref.purpose}` : ''}
          </p>
        )}
      </div>

      {/* Users */}
      <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
        <SignupQrPanel code={code} />

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">People who signed up with this code</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void usersQuery.refetch();
                void codeQuery.refetch();
              }}
              disabled={usersQuery.isFetching}
            >
              <RefreshCw
                className={`h-4 w-4 mr-1 ${usersQuery.isFetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </div>

        {usersQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : usersQuery.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {usersQuery.error instanceof Error ? usersQuery.error.message : 'Failed to load users'}
            </AlertDescription>
          </Alert>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No one has signed up with this code yet.
          </p>
        ) : (
          <div className="border border-border rounded-lg divide-y divide-border">
            {users.map((u) => (
              <CodeUserRow
                key={u.id}
                user={u}
                tiers={tiers}
                code={code}
                onAssigned={() => usersQuery.refetch()}
              />
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function SignupQrPanel({ code }: { code: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);
  // Host is taken dynamically from the running app — never hard-coded.
  const url = `${window.location.origin}/signup?ref=${encodeURIComponent(code)}`;

  function copyLink() {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function downloadPng() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `signup-${code}.png`;
    a.click();
  }

  return (
    <div className="border border-border rounded-lg p-4 flex flex-col sm:flex-row items-start gap-4">
      <div className="bg-white p-2 rounded-md flex-shrink-0">
        <QRCodeCanvas ref={canvasRef} value={url} size={144} marginSize={4} />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-medium">Signup link</h2>
        <p className="text-xs text-muted-foreground mb-2">
          Scan or share to send people to the signup page pre-tagged with this code.
        </p>
        <code className="block text-xs bg-secondary/50 rounded px-2 py-1 break-all">{url}</code>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={copyLink}>
            {copied ? (
              <CheckCircle2 className="h-4 w-4 mr-1" />
            ) : (
              <Copy className="h-4 w-4 mr-1" />
            )}
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPng}>
            <Download className="h-4 w-4 mr-1" /> Download QR
          </Button>
        </div>
      </div>
    </div>
  );
}

function CodeUserRow({
  user,
  tiers,
  code,
  onAssigned,
}: {
  user: SignupRefUser;
  tiers: CommunityTier[];
  code: string;
  onAssigned: () => void;
}) {
  const [pendingTier, setPendingTier] = useState('');
  const [confirming, setConfirming] = useState(false);

  const mutation = useMutation({
    mutationFn: () => setUserTier(user.id, pendingTier, `Assigned via signup code ${code}`),
    onSuccess: () => {
      setConfirming(false);
      setPendingTier('');
      onAssigned();
    },
  });

  function handleAssign() {
    if (!pendingTier) return;
    // Warn + confirm before overriding a subscription-backed (paid) membership.
    if (user.has_subscription && !confirming) {
      setConfirming(true);
      return;
    }
    mutation.mutate();
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{user.full_name ?? '—'}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>

        {/* Current tier + paid/comp */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {user.tier_code ? (
            <Badge variant="default">{user.tier_code}</Badge>
          ) : (
            <Badge variant="outline">no tier</Badge>
          )}
          {user.has_subscription ? (
            <Badge variant="secondary">paid</Badge>
          ) : (
            user.tier_code && <Badge variant="outline">comp</Badge>
          )}
        </div>

        {/* Assign tier */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={pendingTier}
            onChange={(e) => {
              setPendingTier(e.target.value);
              setConfirming(false);
            }}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">— assign tier —</option>
            {tiers.map((t) => (
              <option key={t.id} value={t.code}>
                {t.code} — {t.name}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={handleAssign} disabled={!pendingTier || mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            {confirming ? 'Confirm override' : 'Assign'}
          </Button>
        </div>
      </div>

      {/* Subscription override warning */}
      {confirming && (
        <Alert variant="destructive" className="mt-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This user has an active paid subscription. Assigning a tier overwrites their
            subscription-backed membership and can detach their billing. Click{' '}
            <span className="font-medium">Confirm override</span> to proceed, or change the
            selection to cancel.
          </AlertDescription>
        </Alert>
      )}

      {mutation.isSuccess && (
        <p className="mt-1 text-xs text-green-600 dark:text-green-400 inline-flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> Tier updated.
        </p>
      )}
      {mutation.error && (
        <p className="mt-1 text-xs text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Failed to assign tier'}
        </p>
      )}
    </div>
  );
}
